import PropTypes from 'prop-types';

import { imagePropTypes, selectedOptionsPropTypes } from '../../../propTypes';
import {
  currencyDepositAndTotalPricePropTypesArrayPropTypes,
  currencyWithPriceAndFormattedPriceArrayPropTypes,
} from '../../../util/currency';

const flightClassOption = PropTypes.shape({
  key: PropTypes.string,
  title: PropTypes.string,
  flightClass: PropTypes.string,
  fullDescription: PropTypes.string,
  itineraryDescription: PropTypes.string,
});

const flightOption = PropTypes.shape({
  key: PropTypes.string,
  title: PropTypes.string,
  fullDescription: PropTypes.string,
  itineraryDescription: PropTypes.string,
  outboundFlightNumber: PropTypes.string,
  outboundDepartureAirport: PropTypes.string,
  outboundDepartureAirportCode: PropTypes.string,
  outboundArrivalAirport: PropTypes.string,
  outboundArrivalAirportCode: PropTypes.string,
  outboundDepartureDate: PropTypes.string,
  outboundDepartureTime: PropTypes.string,
  outboundArrivalDate: PropTypes.string,
  outboundArrivalTime: PropTypes.string,
  inboundFlightNumber: PropTypes.string,
  inboundDepartureAirport: PropTypes.string,
  inboundDepartureAirportCode: PropTypes.string,
  inboundArrivalAirport: PropTypes.string,
  inboundArrivalAirportCode: PropTypes.string,
  inboundDepartureDate: PropTypes.string,
  inboundDepartureTime: PropTypes.string,
  inboundArrivalDate: PropTypes.string,
  inboundArrivalTime: PropTypes.string,
});

export const flightClassTourOptionPropTypes = PropTypes.shape({
  key: PropTypes.string,
  pricePerCurrency: currencyWithPriceAndFormattedPriceArrayPropTypes,
  option: flightClassOption,
  soldOut: PropTypes.bool,
  limitedAvailability: PropTypes.bool,
  limitedAvailabilityRemainingSpaces: PropTypes.string,
  limitedAvailabilityText: PropTypes.string,
});

export const flightTourOptionPropTypes = PropTypes.shape({
  key: PropTypes.string,
  price: PropTypes.number,
  option: flightOption,
  flightClasses: PropTypes.arrayOf(flightClassTourOptionPropTypes),
  soldOut: PropTypes.bool,
  limitedAvailability: PropTypes.bool,
  limitedAvailabilityRemainingSpaces: PropTypes.string,
  limitedAvailabilityText: PropTypes.string,
});

export const tourFlightOptionGroupPropTypes = PropTypes.shape({
  key: PropTypes.string,
  day: PropTypes.number,
  subTitle: PropTypes.string,
  defaultOption: flightTourOptionPropTypes,
  upgrades: PropTypes.arrayOf(flightTourOptionPropTypes),
});

const transportOption = PropTypes.shape({
  key: PropTypes.string,
  title: PropTypes.string,
  fullDescription: PropTypes.string,
  itineraryDescription: PropTypes.string,
  outboundDepartureLocation: PropTypes.string,
  outboundArrivalLocation: PropTypes.string,
  outboundDepartureDate: PropTypes.string,
  outboundDepartureTime: PropTypes.string,
  outboundArrivalDate: PropTypes.string,
  outboundArrivalTime: PropTypes.string,
  inboundDepartureLocation: PropTypes.string,
  inboundArrivalLocation: PropTypes.string,
  inboundDepartureDate: PropTypes.string,
  inboundDepartureTime: PropTypes.string,
  inboundArrivalDate: PropTypes.string,
  inboundArrivalTime: PropTypes.string,
});

const tourTransportDepartureOption = PropTypes.shape({
  key: PropTypes.string,
  pricePerCurrency: currencyWithPriceAndFormattedPriceArrayPropTypes,
  option: transportOption,
  soldOut: PropTypes.bool,
  limitedAvailability: PropTypes.bool,
  limitedAvailabilityRemainingSpaces: PropTypes.string,
  limitedAvailabilityText: PropTypes.string,
});

export const tourTransportDepartureOptionGroupPropTypes = PropTypes.shape({
  key: PropTypes.string,
  transportType: PropTypes.string,
  cardTitle: PropTypes.string,
  options: PropTypes.arrayOf(tourTransportDepartureOption),
  soldOut: PropTypes.bool,
  limitedAvailability: PropTypes.bool,
  limitedAvailabilityRemainingSpaces: PropTypes.string,
  limitedAvailabilityText: PropTypes.string,
});

export const tourTransportOptionGroupPropTypes = PropTypes.shape({
  key: PropTypes.string,
  day: PropTypes.number,
  subTitle: PropTypes.string,
  defaultOption: tourTransportDepartureOptionGroupPropTypes,
  upgrades: PropTypes.arrayOf(tourTransportDepartureOptionGroupPropTypes),
});

export const labelsPropType = PropTypes.shape({
  addedToPackageText: PropTypes.string,
  atolProtectedText: PropTypes.string,
  departureAirportText: PropTypes.string,
  extraTitle: PropTypes.string,
  failedToRetrieveCheckoutProcessSettingsMessage: PropTypes.string,
  flightArrivalText: PropTypes.string,
  flightDepartureText: PropTypes.string,
  flightClassTitle: PropTypes.string,
  flightTitle: PropTypes.string,
  invalidPaymentOptionMessage: PropTypes.string,
  invalidTourIdMessage: PropTypes.string,
  limitedAvailabilityText: PropTypes.string,
  miscellaneousExtrasTitle: PropTypes.string,
  perPersonText: PropTypes.string,
  preferredCardImage: imagePropTypes,
  roomsTitle: PropTypes.string,
  sessionInvalidTourIdMessage: PropTypes.string,
  soldOutText: PropTypes.string,
  stadiumButtonText: PropTypes.string,
  submitText: PropTypes.string,
  ticketTitle: PropTypes.string,
  transportTitle: PropTypes.string,
  transportInboundArrivalText: PropTypes.string,
  transportInboundDepartureText: PropTypes.string,
  transportOutboundArrivalText: PropTypes.string,
  transportOutboundDepartureText: PropTypes.string,
});

const itineraryItem = PropTypes.shape({
  content: PropTypes.string,
  key: PropTypes.string,
  type: PropTypes.string,
});

const itinerary = PropTypes.shape({
  date: PropTypes.string,
  day: PropTypes.number,
  key: PropTypes.string,
  items: PropTypes.arrayOf(itineraryItem),
});

export const travelOptionsPackagePropType = PropTypes.shape({
  allowIndividualTicketUpgrade: PropTypes.bool, // appears not to be used
  enforceAllRoomsFilledValidation: PropTypes.bool, // appears not to be used
  enforceUpgradeOfIndividaulTickets: PropTypes.bool, // appears not to be used
  errorCode: PropTypes.number,
  flights: PropTypes.arrayOf(tourFlightOptionGroupPropTypes),
  isFlightsOptional: PropTypes.bool,
  isSoldOut: PropTypes.bool, // appears not to be used
  itinerarySideBar: PropTypes.shape({
    callToBookButtonText: PropTypes.string,
    itinerary: PropTypes.arrayOf(itinerary),
  }),
  limitedAvailability: PropTypes.string, // appears not to be used
  limitedAvailabilityRemainingSpaces: PropTypes.string, // appears not to be used
  limitedAvailabilityText: PropTypes.string, // appears not to be used
  maxPartySize: PropTypes.number, // appears not to be used
  partySizeMax: PropTypes.number, // appears not to be used
  pricePerCurrency: currencyDepositAndTotalPricePropTypesArrayPropTypes,
  selectedOptions: selectedOptionsPropTypes,
  title: PropTypes.string, // appears not to be used
  tourFromDate: PropTypes.string,
  tourToDate: PropTypes.string,
  tourDescription: PropTypes.string, // description
  tournamentName: PropTypes.string, // previously tournament
  url: PropTypes.string, // appears not to be used
  waitingListLink: PropTypes.string, // appears not to be used
  transports: PropTypes.arrayOf(tourTransportOptionGroupPropTypes),
});
